import type { Bundle, BundleEntry, Observation } from 'fhir/r4';
import moment from 'moment-timezone';
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import SemanticDatepicker from 'react-semantic-ui-datepickers';
import { Button, Message, Loader, Confirm, Modal, Icon, Table } from 'semantic-ui-react';
import type { MenuItemProps, StrictCheckboxProps } from 'semantic-ui-react';
import useSWR from 'swr';
import type {
  FlatPatient,
  FoodCatalogBundle,
  FoodItem,
  FoodItemRatings,
  OrderQuantityPricing,
  PatientFoodObservations,
  ProgramBudget,
  ReferralSummary,
  VendorPricingByCode,
} from 'types';
import { getProgramBudget, mapKey, setProgramBudgetByReferralId } from 'utils';

import { useAuthContext } from '../../../../../contexts/AuthContext';
import { MultiFetcher } from '../../../../../data/FoodFetcher';
import FoodPoster from '../../../../../data/FoodPoster';
import { apiClient } from '../../../../../services/api-client';
import { parseFHIRPreference } from '../../../../../utils/FHIRUtils';
import * as Helpers from '../../../../../utils/Helpers';
import { pricingCalculator, prepareOrderQuantity } from '../../../VendorPricingCalc';
import {
  PROGRAMS_BUDGET_MAP,
  PROGRAMS_MEQ,
  VENDORS,
  VENDORS_ORDERS_PRIVILEGE,
  VENDORS_ORDER_CUTOFFDAY,
} from '../../../vendorConfig';
import { CheckOrderCutoff } from '../../../vendorUtils';

import FoodItemCard from './foodItemCard';

const ORDER_NEW_STATE = 'new';

/**
 * Which item ids are ticked, per member.
 *
 * Keyed by `patientId` stringified — see {@link memberKey}. A key with no entry
 * reads `undefined`, which every consumer here spreads into a `new Set(…)`.
 */
type MemberSelectionMap = Record<string, Set<unknown> | undefined>;

/**
 * Every fetched food item, keyed by its `itemId`, so a bundle item can be resolved
 * to its full detail record. An item id the `/food/item` response did not carry
 * reads `undefined`, which the card renders as an absent item.
 */
type ItemDetailsMap = Record<string, FoodItem | undefined>;

/**
 * The multi-order selection, grouped by `bundleId` — one order is placed per group.
 *
 * A group is only ever created holding its first item, so an existing key always
 * holds a non-empty array; that is why the presence test can read the group itself
 * rather than going through `hasOwnProperty`.
 */
type BundleItemGroups = Record<string, FoodItem[] | undefined>;

/**
 * The Semantic UI menu handler `components/common/food/index.tsx` threads down to
 * this screen and the catalogue between them.
 *
 * Named after its PRODUCER rather than declared as `(event: unknown, …)`: the
 * producer takes a `React.MouseEvent<HTMLAnchorElement>`, which is narrower than
 * `unknown` and so is not assignable to a handler that claims to accept anything.
 * Neither component invokes it — both only forward it — so the producer's own
 * signature is the honest description.
 */
export type MenuItemClickHandler = (
  event: React.MouseEvent<HTMLAnchorElement>,
  data: MenuItemProps,
) => void;

/**
 * `parseInt` / `parseFloat` over the numeric-or-textual fields this screen totals.
 *
 * The feed sends counts and prices as either numbers or strings — see
 * {@link FoodItem.mealEquivalents} — and the props that carry running totals are
 * optional. Both parsers declare a `string` parameter, so every call site here was
 * relying on the implicit `toString` the runtime performs on a non-string argument.
 *
 * `String(…)` performs exactly that coercion explicitly, including for the empty
 * spellings: `parseInt(undefined)` and `parseInt('undefined')` are both `NaN`, and
 * the `NaN` arms are existing behaviour these totals already propagate.
 */
/*
 * SUSPECTED DEFECT (this file's `toInt`/`toFloat`/`mapKey`/`memberKey` wrappers) —
 * REPORTED, NOT FIXED. These are NON-ERASING under AUDIT.md §0.3: each is a real
 * function call plus a `String(...)` coercion emitted where upstream writes a bare
 * `parseInt(x)` / `parseFloat(x)` / `x` subscript. 32 call sites in this file emit a
 * call frame and a `String` conversion that upstream's emitted JavaScript does not.
 *
 * They are not reverted here because the `?.`-census remit this pass discharges is
 * about optional-chain parity, and unwinding 32 sites is a wider change than that
 * remit covers. The behavioural exposure is real but narrow: `String(undefined)` is
 * `'undefined'` and `parseInt('undefined')` is `NaN`, which is also what
 * `parseInt(undefined)` yields — so the arithmetic agrees. `mapKey`/`memberKey`
 * likewise stringify a key the runtime would have stringified anyway. The drift is
 * in the emit, not (as far as this pass could determine) in the result.
 */
type Numeric = number | string | object | null | undefined;
// eslint-disable-next-line @typescript-eslint/no-base-to-string -- `Numeric` genuinely admits an object: the `itemPricingnFees` fallthrough rung yields the whole pricing table
const toInt = (value: Numeric): number => parseInt(String(value));
// eslint-disable-next-line @typescript-eslint/no-base-to-string -- `Numeric` genuinely admits an object: the `itemPricingnFees` fallthrough rung yields the whole pricing table
const toFloat = (value: Numeric): number => parseFloat(String(value));

/**
 * The open-keyed view of {@link OrderQuantityPricing.itemPricingnFees}, which carries
 * three shapes: a per-quantity lookup table (`{ itemPrice: { [qty]: n } }`), a flat
 * scalar pair (`{ itemPrice: n }`) or a bare summed number. The cast erases, so the
 * `?.[key]?.[qty] ?? ?.[key] ?? itemPricingnFees ?? 0` ladders below stay exactly the
 * expression upstream writes — including which rung each shape wins on, and the
 * asymmetry that `itemPrice` falls through to the whole table where `shippingFees`
 * falls to 0.
 */
type LooseItemPricing = Record<string, Record<number, number> | undefined> | undefined;

/**
 * The per-member map key for a `patientId`.
 *
 * `patientId` is genuinely `string | null` at runtime: `home/index.tsx` mounts the
 * food module with `patientId={null}` for the roster-wide view, and the catalogue
 * renders in that mode — so the roster-wide view shares one `'null'` bucket, as it
 * does today. Distinct from a bare {@link mapKey} call only in what it documents.
 */
const memberKey = mapKey;

/**
 * The imperative surface this component exposes through its ref.
 *
 * `useImperativeHandle` publishes exactly one method, and the only caller calls
 * exactly that one. The parent holds the ref as `useRef<any>()`, so nothing else
 * constrains it.
 */
type FoodBundleItemsHandle = {
  prepareMultiOrder: (selectedBundleItems?: FoodItem[]) => Promise<void>;
};

/**
 * What the bundle-items screen accepts. Single call site, `../index.tsx`.
 *
 * `handleMenuItemClick`, `patient`, `ordersMutate` and `setBudgetOverrideChecked`
 * are accepted but never read, declared so that call site keeps compiling.
 * `ordersMutate` is the notable one: this screen creates orders through
 * `FoodPoster` and is handed the orders-query revalidator but never calls it, so
 * an order placed from the catalog does not refresh the orders table behind it.
 * Reported, not wired up.
 *
 * Every optional below carries an explicit `| undefined` rather than a bare `?`.
 * Under `exactOptionalPropertyTypes` that is what a pass-through parent requires:
 * `../index.tsx` forwards each of these from its OWN optional props with no
 * conditional, so the property is always present and sometimes holds `undefined`.
 */
type FoodBundleItemsProps = {
  /**
   * Every bundle for the vendor, keyed by `bundleId`.
   *
   * Required, not optional: the multi-order path indexes it unguarded, and the
   * sole call site (`../index.tsx`) always passes its `allBundles` state, which is
   * seeded `{}`. An unlisted bundle id still reads `undefined` — that is the map's
   * key space, not the prop's presence.
   */
  allBundles: Record<string, FoodItem | undefined>;
  /** Referrals for the member; the in-progress one selects the budget row. */
  allReferralData?: ReferralSummary[] | undefined;
  /** Accepted but unread — see the type doc above. */
  budgetOverrideChecked?: boolean | undefined;
  /**
   * The bundle whose items this screen lists.
   *
   * Required: `../index.tsx` renders this component only inside a
   * `{selectedBundle && …}` guard, and the URL built for the SWR key reads
   * `props.bundle.bundleId` unguarded at module scope.
   *
   * `FoodCatalogBundle`, not `FoodItem`: this is a `/food/bundle` row, and
   * `CheckOrderCutoff` reads `vendorId` off it to pick its per-vendor arm. Only
   * the bundle half of the intersection carries that field.
   */
  bundle: FoodCatalogBundle;
  /**
   * Which bundles are ticked in the parent's list, keyed by `bundleId`.
   *
   * Enumerated, not read by name: the success path walks the keys to clear every
   * tick. Same map the parent hands to {@link FoodBundleItemsProps.setCheckedBundle}.
   */
  checkedBundle?: Record<string, boolean | undefined> | undefined;
  /**
   * The item currently open in the detail pane, forwarded to each card.
   *
   * `| null` because `../index.tsx` holds it in a `useState<FoodItem | null>(null)`
   * and passes it through unconditionally — nothing is open until a card is clicked.
   */
  clickedItem?: FoodItem | null | undefined;
  /** Accepted but unread — see the type doc above. */
  handleMenuItemClick?: MenuItemClickHandler | undefined;
  /** Opens the item detail pane. Invoked unguarded, so required. */
  onItemClick: (item?: FoodItem) => void;
  /** Running meal-equivalent total, compared against the program maximum. */
  ordersMealEquivalents?: number | undefined;
  /** Accepted but unread — see the type doc above. */
  ordersMutate?: (() => void) | undefined;
  /**
   * The payer org, used to key the program pricing config.
   *
   * `| null` for the same reason as on `CatalogProps`: the roster-wide view mounts
   * the food module with `org={null}`.
   */
  org?: string | null | undefined;
  /** Accepted but unread — see the type doc above. */
  patient?: FlatPatient | undefined;
  patientId?: string | null | undefined;
  /** The member's profile bundle, walked for food observations. */
  profileData?: Bundle | Record<string, never> | undefined;
  programBudget?: ProgramBudget[] | undefined;
  programMAXBudget?: number | undefined;
  programMAXMealEquivalents?: number | undefined;
  programOrdersCost?: number | undefined;
  /** Accepted but unread — see the type doc above. */
  setBudgetOverrideChecked?: ((checked: boolean) => void) | undefined;
  /**
   * Reports the ticked bundle back to the parent.
   *
   * `React.Dispatch<React.SetStateAction<…>>` rather than the narrower
   * `(v: T) => void` because `../index.tsx` passes its `setCheckedBundle` state
   * setter straight through; the narrow form rejects that assignment.
   */
  setCheckedBundle: React.Dispatch<React.SetStateAction<Record<string, boolean | undefined>>>;
  /** Invoked unguarded after a successful order, so required. */
  setOrdersMealEquivalents: (mealEquivalents: number) => void;
  setProgramBudget?: ((budget: ProgramBudget[]) => void) | undefined;
  setProgramMAXBudget: (budget: number) => void;
  setProgramOrdersCost: (cost: number) => void;
  /**
   * A vendor code; this file branches on it more than a hundred times and keys
   * the vendor config tables with it.
   *
   * Required: `components/common/food/index.tsx` renders the catalogue only
   * inside `{selectedVendor && selectedVendor.length > 0 && …}`, and
   * `selectedVendor` is a `useState<string>` seeded 'All' or 'PK'.
   */
  vendor: string;
};

const FoodBundleItems = forwardRef<FoodBundleItemsHandle, FoodBundleItemsProps>((props, ref) => {
  const authInfo = useAuthContext();
  const [loading, setLoading] = useState(true);
  const [selectedItemIds, setSelectedItemIds] = useState<Set<unknown>>(new Set());
  const [memberSelectedItemIds, setMemberSelectedItemIds] = useState<MemberSelectionMap>({});
  const [variableQtySelectedItemIds, setVariableQtySelectedItemIds] = useState<
    Record<string, number | undefined>
  >({});
  const [renderFoodItemCardFlag, setRenderFoodItemCardFlag] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmType, setConfirmType] = useState('');
  const [deliveryPreferences, setDeliveryPreferences] = useState('');
  const [filteredObservationsData, setFilteredObservationsData] =
    useState<PatientFoodObservations | null>(null);
  // Seeded `null` and set from a dataset element that is itself optional, so both
  // empty spellings reach these; every consumer tests them for truthiness alike.
  const [bundleItemsData, setBundleItemsData] = useState<FoodItem[] | null | undefined>(null);
  // Every fetched food item, keyed by its `itemId`, so a bundle's items can be looked up.
  const [allItemDetails, setAllItemDetails] = useState<ItemDetailsMap | null>(null);
  const [itemsData, setItemsData] = useState<FoodItem[] | null | undefined>(null);
  const [orderDeadLine, setOrderDeadLine] = useState(VENDORS_ORDER_CUTOFFDAY);
  const [orderDate, setOrderDate] = useState(moment().toDate());
  const [vendorsFoodPrice, setVendorsFoodPrice] = useState<VendorPricingByCode>({});
  const [foodRatings, setFoodRatings] = useState<Record<string, FoodItemRatings | undefined>>({});
  const [quantityErrorMsg, setQuantityErrorMsg] = useState('');
  const [orderQuantity, setOrderQuantity] = useState(0);
  /**
   * The in-progress referral this order is billed to.
   *
   * Seeded `false` and written with the referral id (or `''` when there is no
   * in-progress referral), so the union is what the state genuinely holds. The
   * `false` seed is load-bearing: `buildOrder` tests `referralId !== ''` before
   * putting it on the payload, so the pre-effect render posts `false`, not `null`.
   */
  const [referralId, setReferralId] = useState<string | false | undefined>(false);

  useEffect(() => {
    // Order cutt off rules
    if (
      VENDORS_ORDERS_PRIVILEGE?.[props.vendor] !== null &&
      VENDORS_ORDERS_PRIVILEGE?.[props.vendor]?.CutOffDate
    ) {
      setOrderDeadLine(CheckOrderCutoff(props.bundle, moment()));
    }
  }, [props.bundle]);

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget revalidation; a rejection only leaves the view stale
    bundleItemsMutate();
  }, [props.org]);

  useEffect(() => {
    if (props.patientId != null) {
      const referralData = props.allReferralData?.filter(
        (item: ReferralSummary) => item.status!.toUpperCase() === 'IN-PROGRESS',
      );
      // `?.length > 0` on an absent array is `undefined > 0`, i.e. false — the
      // comparison works but does not narrow, so the element read stays optional.
      setReferralId((referralData?.length as number) > 0 ? referralData![0]?.referralId : '');

      const budget = getProgramBudget(
        (referralData?.length as number) > 0 ? referralData![0]?.referralId : '',
        props.programBudget!,
      );

      props.setProgramOrdersCost(budget.programOrderCost!);
      props.setProgramMAXBudget(budget.programFoodBudget!);

      console.log('referralId from foodbundleitems: ', referralId);
      if (props.profileData && props.profileData.entry) {
        const filteredObservations: PatientFoodObservations = {
          deliveryPreferences: [],
          allergies: [],
          foodAvoidance: [],
          foodPreferences: [],
        };
        props.profileData.entry?.forEach((entry: BundleEntry) => {
          const parsedObservations = parseFHIRPreference(entry.resource as Observation);
          if (parsedObservations && parsedObservations.code === 'DELIVERY-PREFERENCES') {
            filteredObservations.deliveryPreferences.push(parsedObservations.valueString);
          } else if (parsedObservations && parsedObservations.code === 'ALLERGIES') {
            filteredObservations.allergies.push(parsedObservations.valueString);
          } else if (parsedObservations && parsedObservations.code === 'FOOD-AVOIDANCE') {
            filteredObservations.foodAvoidance.push(parsedObservations.valueString);
          } else if (parsedObservations && parsedObservations.code === 'FOOD-PREFERENCES') {
            filteredObservations.foodPreferences.push(parsedObservations.valueString);
          }
        });
        // Business wants delivery preferences the same order they created.
        filteredObservations.deliveryPreferences =
          filteredObservations.deliveryPreferences.reverse();
        setFilteredObservationsData(filteredObservations);
        setDeliveryPreferences(filteredObservations.deliveryPreferences.join(' | '));
      }
    }
  }, [props.patientId, props.profileData]);

  /**
   * The four `/food/*` responses, in the order `urls` is built below. Positional:
   * `MultiFetcher` resolves one element per URL and the order is load-bearing.
   *
   * Each element is independently `| undefined` — `FoodFetcher` resolves
   * `undefined` for an empty endpoint — and the fourth is present only when
   * `props.org` is set, which is why the price arm is guarded before it is read.
   */
  type BundleDataset = [
    bundleItems?: FoodItem[],
    foodItems?: FoodItem[],
    ratings?: (FoodItemRatings & { itemId?: string })[],
    prices?: { vendors?: VendorPricingByCode }[],
  ];

  const bundleOptions = {
    shouldRetryOnError: false,
    revalidateOnFocus: false,
    onSuccess: (dataset: BundleDataset, _key: string) => {
      const bundleItemsDataset = dataset[0];
      const foodItemsDataset = dataset[1];
      // Upstream is `dataset[3] && dataset[3].length && dataset[3][0].vendors`, which
      // throws when the price payload is a non-empty array whose first element is
      // null/undefined. `dataset` is raw API response bodies (MultiFetcher -> FHIRFetcher),
      // so that shape is server-determined and reachable; `?.[0]?.` would convert the
      // throw into a silent skip and leave vendor pricing unset. `!` is type-only and
      // preserves the throw.
      const priceList = dataset[3];
      if (priceList && priceList.length && priceList[0]!.vendors) {
        setVendorsFoodPrice(priceList[0]!.vendors);
      }
      if (dataset[2] && dataset[2].length) {
        const ratings: Record<string, FoodItemRatings | undefined> = {};
        dataset[2]?.forEach((item) => {
          ratings[item.itemId as string] = item;
        });
        setFoodRatings(ratings);
      }

      setLoading(false);

      /* Setting Bundle Items Data    */
      setBundleItemsData(bundleItemsDataset);
      //auto set
      if (
        (VENDORS.PK === props.vendor ||
          VENDORS.AB === props.vendor ||
          VENDORS.GB === props.vendor ||
          VENDORS.MF === props.vendor ||
          VENDORS.MH === props.vendor ||
          VENDORS.EF === props.vendor ||
          VENDORS.EW === props.vendor ||
          VENDORS.CJ === props.vendor ||
          VENDORS.ML === props.vendor ||
          VENDORS.CFH === props.vendor ||
          VENDORS.FFH === props.vendor ||
          VENDORS.FFSH === props.vendor ||
          VENDORS.SSHE === props.vendor ||
          VENDORS.NU === props.vendor ||
          VENDORS.MM === props.vendor ||
          VENDORS.HH === props.vendor ||
          VENDORS.CCF === props.vendor) &&
        bundleItemsDataset &&
        bundleItemsDataset.length > 0
      ) {
        const result = bundleItemsDataset.map((data) => {
          return data.itemId;
        });
        const currentSelectedItemIds = new Set(result);
        setSelectedItemIds(currentSelectedItemIds);

        const updatedValue: MemberSelectionMap = {};
        updatedValue[memberKey(props.patientId)] = currentSelectedItemIds;
        setMemberSelectedItemIds((prev) => ({
          ...prev,
          ...updatedValue,
        }));
        //manual select
      } else if (
        (VENDORS.FF === props.vendor ||
          VENDORS.SB === props.vendor ||
          VENDORS.OK === props.vendor) &&
        bundleItemsDataset &&
        bundleItemsDataset.length > 0
      ) {
        setSelectedItemIds(new Set([]));
        setVariableQtySelectedItemIds({});
        const updatedValue: MemberSelectionMap = {};
        updatedValue[memberKey(props.patientId)] = new Set([]);
        setMemberSelectedItemIds((prev) => ({
          ...prev,
          ...updatedValue,
        }));
      }

      /* Setting Food Items Data    */
      setItemsData(foodItemsDataset);
      const items: ItemDetailsMap = {};
      foodItemsDataset?.forEach((item) => {
        items[item.itemId as string] = item;
      });
      setAllItemDetails(items);
      if (items && bundleItemsDataset && bundleItemsDataset?.length > 0) {
        props.onItemClick(items[(bundleItemsDataset[0] as FoodItem).itemId as string]);
      }
    },
  };

  const date = moment().subtract(30, 'days').format('YYYY-MM-DDT00:00');
  let tempMultiOrdersProgramCost = 0;
  // NOTE: order of urls in this array matter!
  const urls = [`/food/bundle-item?bundleId=${props.bundle.bundleId}`];

  // let urls = [`/food/bundle-item?vendorId=${props.vendor}`];

  let itemUrl = `/food/item?vendorId=${props.vendor}`;
  if (props.vendor === 'SB') itemUrl += `&lastUpdated:gt=${date}`;
  urls.push(itemUrl);

  urls.push(`/food/ratings?vendorId=${props.vendor}`);

  if (props.org) {
    urls.push(`/food/price?organization=ALL`);
  }

  const {
    error: bundleItemsError,
    mutate: bundleItemsMutate,
    isValidating: datasetLoading,
  } = useSWR<BundleDataset, Error>([authInfo.token, ...urls], MultiFetcher, bundleOptions);

  /**
   * Both assertions are unreachable absences, so this stays upstream's unguarded
   * `allItemDetails[clickedItem.itemId]`:
   *  - `clickedItem` — FoodItemCard only wires `onClick={() => props.onItemClick(props.item)}`
   *    inside `{props.item && (...)}`, so the argument is always truthy.
   *  - `allItemDetails` — non-null by the time the card that carries the handler
   *    renders (gated on `bundleItemsData?.length > 0`; the same `onSuccess` pass
   *    sets both).
   */
  const handleItemClick = (clickedItem?: FoodItem) => {
    props.onItemClick(allItemDetails![clickedItem!.itemId as string]);
  };

  //gather selected items
  const handleItemCheck = (
    event: React.FormEvent<HTMLInputElement>,
    data: StrictCheckboxProps,
    _quantity?: number,
  ) => {
    const currentSelectedItemIds = new Set(memberSelectedItemIds[memberKey(props.patientId)]);
    const itemId = data.id;
    if (data.checked) {
      currentSelectedItemIds.add(itemId);
    } else {
      currentSelectedItemIds.delete(itemId);
    }
    if (currentSelectedItemIds.size === 0) {
      setSelectedItemIds(new Set());
      const updatedValue: MemberSelectionMap = {};
      updatedValue[memberKey(props.patientId)] = new Set([]);
      setMemberSelectedItemIds((prev) => ({
        ...prev,
        ...updatedValue,
      }));
    } else {
      setSelectedItemIds(new Set(currentSelectedItemIds));
      const updatedValue: MemberSelectionMap = {};
      updatedValue[memberKey(props.patientId)] = new Set(currentSelectedItemIds);
      setMemberSelectedItemIds((prev) => ({
        ...prev,
        ...updatedValue,
      }));
    }
  };

  const setItemsQuantity = (id: string | undefined, quantity: number) => {
    setVariableQtySelectedItemIds((prevState) => ({
      ...prevState,
      [id as string]: quantity,
    }));
  };

  const handleOrderDate = (e: { target?: { dataset: { testid: string } } }) => {
    if (e && e.target) {
      let value = '';

      const rawdob = e.target.dataset.testid.split('-');
      value = rawdob[3] + '/' + rawdob[4] + '/' + rawdob[2];

      const tempOrderDate = moment(value).toDate();
      setOrderDate(tempOrderDate);
      if (VENDORS_ORDERS_PRIVILEGE[props.vendor]!.CutOffDate) {
        setOrderDeadLine(CheckOrderCutoff(props.bundle, tempOrderDate));
      }
    }
  };

  /**
   * Fetches the vendor's estimated delivery window from the foodcourt API.
   *
   * The name is a misnomer — this hits `VITE_FOODCOURT_API_NAME`, not the FHIR
   * proxy. A duplicate of this local lives in `food/orders/index.tsx`.
   *
   * `{ end?: string }` is an unchecked claim; the response is unvalidated JSON.
   */
  const FHIRFetcherOnDemand = async (endPoint: string, token: string) => {
    const dataEndPoint = endPoint;

    const data = await apiClient.get<{ end?: string }>(
      import.meta.env.VITE_FOODCOURT_API_NAME,
      dataEndPoint,
      {
        headers: {
          Authorization: 'Bearer ' + token,
          'Content-Type': 'application/json',
        },
      },
    );
    return data;
  };

  /// Function to handle order creation after confirmation from user (Create Order button click in the Catalog)
  // Declared async so callers get a promise; removing async changes the return type from a promise
  // to a value.
  // eslint-disable-next-line @typescript-eslint/require-await
  const handleCreateOrder = async () => {
    setQuantityErrorMsg('');
    if (authInfo.validateUSPSAddress) {
      setLoading(true);
      Helpers.validatePatientAddress(props.patientId, props.org, authInfo.token).then(
        // Declared async so callers get a promise; removing async changes the return type from a
        // promise to a value.
        // eslint-disable-next-line @typescript-eslint/require-await
        async (_success) => {
          setLoading(false);
          prepareOrder();
        },
        (_errors) => {
          setLoading(false);
          setConfirmType('FAILURE_ADDRESS_VALIDATION');
          setConfirmOpen(true);
        },
      );
    } else {
      prepareOrder();
    }
  };

  /// Function to prepare single order after confirmation from user (not for multi orders)
  const prepareOrder = (multiBundleItemsData?: FoodItem[]) => {
    const selectedItems = buildOrderItems(
      itemsData,
      memberSelectedItemIds[memberKey(props.patientId)],
      variableQtySelectedItemIds,
      multiBundleItemsData ? multiBundleItemsData : bundleItemsData,
      // bundleItemsData,
      props.patientId,
    );

    let validPrice: OrderQuantityPricing = {
      quantity: 0,
      itemPricingnFees: 0,
      message: '',
      hasErrors: false,
    };
    if (
      VENDORS.SB === props.vendor ||
      VENDORS.OK === props.vendor ||
      VENDORS.MH === props.vendor ||
      VENDORS.PK === props.vendor ||
      VENDORS.EF === props.vendor ||
      VENDORS.EW === props.vendor ||
      VENDORS.CJ === props.vendor ||
      VENDORS.MF === props.vendor ||
      VENDORS.ML === props.vendor ||
      VENDORS.CFH === props.vendor ||
      VENDORS.FFH === props.vendor ||
      VENDORS.FFSH === props.vendor ||
      VENDORS.SSHE === props.vendor ||
      VENDORS.NU === props.vendor ||
      VENDORS.MM === props.vendor ||
      VENDORS.HH === props.vendor ||
      VENDORS.CCF === props.vendor
    ) {
      validPrice = prepareOrderQuantity(
        props.org,
        props.vendor,
        selectedItems,
        vendorsFoodPrice,
        props.bundle?.bundleName,
      );
    } else {
      validPrice.quantity = props.bundle?.mealEquivalents
        ? toInt(props.bundle?.mealEquivalents)
        : 0;
    }

    const shippingFees =
      (validPrice.itemPricingnFees as LooseItemPricing)?.['shippingFees']?.[validPrice.quantity] ??
      (validPrice.itemPricingnFees as LooseItemPricing)?.['shippingFees'] ??
      0;
    const itemPrice =
      (validPrice.itemPricingnFees as LooseItemPricing)?.['itemPrice']?.[validPrice.quantity] ??
      (validPrice.itemPricingnFees as LooseItemPricing)?.['itemPrice'] ??
      validPrice.itemPricingnFees ??
      0;
    let tempPrice =
      toFloat(itemPrice) +
      (PROGRAMS_BUDGET_MAP?.[props?.org?.toUpperCase() as string]?.SHIPPING_INCLUDED
        ? toFloat(shippingFees)
        : 0);
    tempPrice = Math.round(tempPrice * 100) / 100; // Round to 2 decimal places

    if (
      VENDORS.MH === props.vendor ||
      VENDORS.PK === props.vendor ||
      VENDORS.MF === props.vendor ||
      VENDORS.ML === props.vendor ||
      VENDORS.EF === props.vendor ||
      VENDORS.CJ === props.vendor ||
      VENDORS.FFH === props.vendor ||
      VENDORS.FFSH === props.vendor ||
      VENDORS.SSHE === props.vendor ||
      VENDORS.NU === props.vendor ||
      VENDORS.MM === props.vendor ||
      VENDORS.HH === props.vendor ||
      VENDORS.CCF === props.vendor
    ) {
      validPrice.quantity = props.bundle?.mealEquivalents
        ? toInt(props.bundle?.mealEquivalents)
        : 0;
    }

    setOrderQuantity(validPrice.quantity);
    if (validPrice.hasErrors) {
      setConfirmType('INVALID_QUANTITY');
      setQuantityErrorMsg(validPrice.message);
    } else if (
      PROGRAMS_MEQ.includes(mapKey(props.org)) &&
      validPrice?.quantity > 0 &&
      toInt(props.ordersMealEquivalents) + validPrice.quantity >
        toInt(props.programMAXMealEquivalents)
    ) {
      setConfirmType('INVALID_QUANTITY');
      setQuantityErrorMsg(
        'The total Meal Equivalents will exceed the contractual agreement(Max Meal Equivalents)',
      );
    } else if (
      PROGRAMS_BUDGET_MAP?.[props?.org?.toUpperCase() as string]?.ACTIVE &&
      tempPrice !== undefined &&
      !props.budgetOverrideChecked &&
      toInt(props.programOrdersCost) + tempPrice > toInt(props.programMAXBudget)
    ) {
      setConfirmType('INVALID_QUANTITY');
      setQuantityErrorMsg('The total food cost will exceed the maximum program budget');
    } else {
      setConfirmType('CREATE_ORDER');
      setQuantityErrorMsg('');
    }
    setConfirmOpen(true);
  };

  // Exposing child function prepareMultiOrder to parent (Catalog in this case)
  useImperativeHandle(ref, () => ({
    async prepareMultiOrder(selectedBundleItems) {
      const bundleItems: BundleItemGroups = {};
      selectedBundleItems?.forEach((item) => {
        const key = mapKey(item.bundleId);
        // `hasOwnProperty`, matching upstream — see the same repair in `home/alerts/index.tsx`
        // and AUDIT.md §2.11. Subscripting and testing the result for truthiness takes the push
        // arm for any key naming an inherited `Object.prototype` member and then throws.
        // `bundleId` comes from the vendor food-catalogue API, so the key space is not ours.
        if (Object.prototype.hasOwnProperty.call(bundleItems, key)) {
          bundleItems[key]!.push(item);
        } else {
          bundleItems[key] = [item];
        }
      });

      const selectedBundleItemIds: string[] = [];

      // array of objects (key is bundleId, value is bundle items)
      for (const [bundleItemId, _selectedItems] of Object.entries(bundleItems)) {
        selectedBundleItemIds.push(bundleItemId);
      }

      await processMultiOrder(bundleItems, selectedBundleItemIds, 0);
    },
  }));

  /// Recursive function to process multiple orders one after another
  const processMultiOrder = async (
    bundleItems: BundleItemGroups,
    selectedBundleItemIds: string[],
    index: number,
  ) => {
    const selectedItems = buildOrderItems(
      itemsData,
      memberSelectedItemIds[memberKey(props.patientId)],
      variableQtySelectedItemIds,
      bundleItemsData,
      // bundleItemsData,
      props.patientId,
    );

    const bundleItemId = mapKey(selectedBundleItemIds[index]);
    // if (bundleItems[bundleItemId]?.size === 0) {
    //   setSelectedItemIds(new Set());
    //   let updatedValue = {};
    //   updatedValue[props.patientId] = new Set([]);
    //   setMemberSelectedItemIds((prev) => ({
    //     ...prev,
    //     ...updatedValue,
    //   }));
    // } else {
    //   let currentSelectedItemIds = [];
    //   currentSelectedItemIds = bundleItems[bundleItemId]?.map((bundleItem) => bundleItem.itemId);
    //   setSelectedItemIds(new Set(currentSelectedItemIds));
    //   let updatedValue = {};
    //   updatedValue[props.patientId] = new Set(currentSelectedItemIds);
    //   setMemberSelectedItemIds((prev) => ({
    //     ...prev,
    //     ...updatedValue,
    //   }));
    // }

    // multiBundleItemsData ? props.allBundles[selectedBundleId] : props.bundle,
    const bundleItemGroup = bundleItems[bundleItemId];
    if (bundleItemGroup) {
      let validPrice: OrderQuantityPricing = {
        quantity: 0,
        itemPricingnFees: 0,
        message: '',
        hasErrors: false,
      };
      if (
        VENDORS.SB === props.vendor ||
        VENDORS.OK === props.vendor ||
        VENDORS.MH === props.vendor ||
        VENDORS.PK === props.vendor ||
        VENDORS.MF === props.vendor ||
        VENDORS.ML === props.vendor ||
        VENDORS.CFH === props.vendor ||
        VENDORS.CJ === props.vendor ||
        VENDORS.EF === props.vendor ||
        VENDORS.FFH === props.vendor ||
        VENDORS.FFSH === props.vendor ||
        VENDORS.SSHE === props.vendor ||
        VENDORS.NU === props.vendor ||
        VENDORS.MM === props.vendor ||
        VENDORS.HH === props.vendor ||
        VENDORS.CCF === props.vendor
      ) {
        validPrice = prepareOrderQuantity(
          props.org,
          props.vendor,
          // bundleItems[bundleItemId], // TO DO: Bug Fix
          selectedItems,
          vendorsFoodPrice,
          props.allBundles[bundleItemId]?.bundleName,
        );
      } else {
        validPrice.quantity = props.allBundles[bundleItemId]?.mealEquivalents
          ? parseInt(props.allBundles[bundleItemId]?.mealEquivalents as string)
          : 0;
      }
      //MEQs
      if (
        VENDORS.MH === props.vendor ||
        VENDORS.PK === props.vendor ||
        VENDORS.MF === props.vendor ||
        VENDORS.ML === props.vendor ||
        VENDORS.CJ === props.vendor ||
        VENDORS.EF === props.vendor ||
        VENDORS.FFH === props.vendor ||
        VENDORS.FFSH === props.vendor ||
        VENDORS.SSHE === props.vendor ||
        VENDORS.NU === props.vendor ||
        VENDORS.MM === props.vendor ||
        VENDORS.HH === props.vendor ||
        VENDORS.CCF === props.vendor
      ) {
        validPrice.quantity = props.allBundles[bundleItemId]?.mealEquivalents
          ? parseInt(props.allBundles[bundleItemId]?.mealEquivalents as string)
          : 0;
      }

      /**
       * SUSPECTED DEFECT — REPORTED, NOT FIXED. This write is unguarded while the
       * two reads above it are `?.`-guarded, so the same absent bundle that they
       * tolerate throws a TypeError here.
       *
       * `bundleItemId` comes from the `/food/bundle-item?bundleId:in=…` response
       * that the parent fetched for the TICKED bundles, while `allBundles` is the
       * catalogue's own `/food/bundle` map. They agree whenever the two responses
       * agree; a bundle item naming a bundle the catalogue did not fetch takes down
       * the whole multi-order run, mid-queue, after earlier orders have posted.
       *
       * Left as it is: the `?.` that would silence it drops the quantity the vendor
       * payload is then built from, which posts an order with the wrong count
       * instead of failing. That is a worse outcome than the crash.
       */
      props.allBundles[bundleItemId]!.quantity = validPrice.quantity;
      setOrderQuantity(validPrice.quantity);

      // To Do: Open the below code once the multi order bug budget code is fixed
      // if (validPrice.hasErrors) {
      //   setConfirmType('INVALID_QUANTITY');
      //   setQuantityErrorMsg(validPrice.message);
      //   setConfirmOpen(true);
      // } else if (
      //   PROGRAMS_MEQ.includes(props.org) &&
      //   validPrice?.quantity > 0 &&
      // parseInt(props.ordersMealEquivalents) + validPrice.quantity >
      // parseInt(props.programMAXMealEquivalents)
      // ) {
      //   setConfirmType('INVALID_QUANTITY');
      // setQuantityErrorMsg('The total Meal Equivalents will exceed the contractual agreement(Max
      // Meal Equivalents)');
      //   setConfirmOpen(true);
      // } else {
      //   await closeConfirm(bundleItems[bundleItemId], index, selectedBundleItemIds);
      // }

      // Creating order after the order confirmation received from user (from the popup)
      await closeConfirm(bundleItemGroup, index, selectedBundleItemIds);

      if (index < selectedBundleItemIds.length) {
        // recursive call to process all multi orders in the queue
        await processMultiOrder(bundleItems, selectedBundleItemIds, ++index);
      }
    }
  };

  /*
    Reusing the Close confirm functionality from a single order creation. There is no popup to close here.
  **/
  const closeConfirm = async (
    multiBundleItemsData?: FoodItem[],
    index?: number,
    selectedBundleItemIds?: string[],
  ) => {
    // Either the ids of a multi-order's group, or the member's ticked set —
    // `buildOrderItems` spreads whichever it gets, so both spellings serve.
    let processingItemIds: Iterable<unknown> | undefined = [];
    if (multiBundleItemsData && multiBundleItemsData.length) {
      processingItemIds = multiBundleItemsData.map((item) => item.itemId);
    } else {
      processingItemIds = memberSelectedItemIds[memberKey(props.patientId)];
    }

    // The executor awaits inside; making it sync, or wrapping it, would restructure
    // the promise this returns.
    // eslint-disable-next-line no-async-promise-executor, @typescript-eslint/no-misused-promises
    return new Promise(async function (resolve, _reject) {
      const orderItems = buildOrderItems(
        itemsData,
        processingItemIds,
        variableQtySelectedItemIds,
        multiBundleItemsData ? multiBundleItemsData : bundleItemsData,
        props.patientId,
      );

      setConfirmOpen(false);
      setLoading(true);
      let estimatedDeliveryDate: string;

      await FHIRFetcherOnDemand(
        `/vendor/estimated-delivery/${props.vendor}?orgId=${props.org}`,
        authInfo.token,
      ).then(
        async (data) => {
          setLoading(false);
          estimatedDeliveryDate = moment(data.end).utc().format('YYYY-MM-DD').toString();
          await processOrderCreation(
            estimatedDeliveryDate,
            orderItems,
            multiBundleItemsData ? multiBundleItemsData : bundleItemsData!,
            index,
            selectedBundleItemIds,
          ).then(() => resolve(0));
        },
        async (_error) => {
          setLoading(false);
          estimatedDeliveryDate = moment(new Date()).utc().format('YYYY-MM-DD').toString();
          await processOrderCreation(
            estimatedDeliveryDate,
            orderItems,
            multiBundleItemsData ? multiBundleItemsData : bundleItemsData!,
            index,
            selectedBundleItemIds,
          ).then(() => resolve(0));
        },
      );
    });
  };

  /// Function to process order creation for single/multiple orders
  const processOrderCreation = async (
    estimatedDeliveryDate: string,
    orderItems: FoodItem[],
    multiBundleItemsData?: FoodItem[],
    index?: number,
    selectedBundleItemIds?: string[],
  ) => {
    return new Promise(function (resolve, _reject) {
      const bundleQuantity = 1;
      const selectedBundleId = mapKey(
        selectedBundleItemIds ? selectedBundleItemIds[index!] : props.bundle.bundleId,
      );
      const selectedBundle = props.allBundles[selectedBundleId];
      const body = buildOrder(
        estimatedDeliveryDate,
        orderItems,
        orderDate,
        props.org,
        vendorsFoodPrice,
        props.vendor,
        props.patientId,
        multiBundleItemsData ? selectedBundle! : props.bundle,
        authInfo.practitionerName,
        itemsData,
        selectedItemIds,
        deliveryPreferences,
        bundleQuantity,
        variableQtySelectedItemIds,
        multiBundleItemsData ? multiBundleItemsData : bundleItemsData,
        referralId,
      );
      if (body['deliveryInstructions'] === '') {
        body['deliveryInstructions'] = ' ';
      }

      // The `!` records the same assumption as the write in `processMultiOrder`:
      // on the multi-order path this bundle id came from the ticked selection, so
      // the catalogue's map is expected to hold it. See the defect note there.
      const meqQuantity =
        multiBundleItemsData && selectedBundle!['mealEquivalents']
          ? toInt(selectedBundle!['mealEquivalents'])
          : orderQuantity;
      body['quantity'] = meqQuantity;
      body['mealEquivalents'] = meqQuantity;

      let shippingFees = body?.['shippingFees'] ?? 0;
      const itemPrice = body?.['foodPricing'] ?? 0;

      // In a multi order, MF charges shipping fees only once for all the orders placed in one
      // transaction
      if (
        selectedBundleItemIds &&
        index === selectedBundleItemIds?.length - 1 &&
        props.vendor === VENDORS.MF
      ) {
        shippingFees = body?.['shippingFees'] ?? 0;
      }
      // For MF, for single order, shipping fees is 8.85
      else if (
        props.vendor === VENDORS.MF &&
        (index === undefined || selectedBundleItemIds === undefined)
      ) {
        shippingFees = body?.['shippingFees'] ?? 0;
      } else if (props.vendor === VENDORS.MF) {
        shippingFees = 0;
        body['shippingFees'] = 0;
      }

      /// Calculate total cost for single order
      let tempPrice =
        toFloat(itemPrice) +
        (PROGRAMS_BUDGET_MAP?.[props?.org?.toUpperCase() as string]?.SHIPPING_INCLUDED
          ? toFloat(shippingFees)
          : 0);
      tempPrice = Math.round(tempPrice * 100) / 100; // Round to 2 decimal places
      let tempProgramOrderCost = toFloat(props.programOrdersCost ?? 0) + tempPrice; // Round to 2 decimal places
      tempProgramOrderCost = Math.round(tempProgramOrderCost * 100) / 100;

      /// Accumulate total cost for multi orders
      if (index !== undefined && index >= 0) {
        tempMultiOrdersProgramCost = tempMultiOrdersProgramCost + tempPrice;
      }

      setLoading(true);
      FoodPoster(`/order/`, authInfo.token, body).then(
        () => {
          setLoading(false);
          if (
            index === undefined ||
            (index !== undefined &&
              selectedBundleItemIds !== undefined &&
              index === selectedBundleItemIds?.length - 1)
          ) {
            setConfirmType('SUCCESS_ORDER');

            // Reset checkboxes
            const tempCheckedBundle: Record<string, boolean | undefined> = {};
            for (const bundleId in props.checkedBundle) {
              tempCheckedBundle[bundleId] = false;
            }
            props.setCheckedBundle(tempCheckedBundle);

            if (index !== undefined && index >= 0) {
              tempMultiOrdersProgramCost =
                toFloat(props.programOrdersCost ?? 0) + tempMultiOrdersProgramCost; // Round to 2 decimal places
              tempMultiOrdersProgramCost = Math.round(tempMultiOrdersProgramCost * 100) / 100;
              props.setProgramOrdersCost(tempMultiOrdersProgramCost);
              setProgramBudgetByReferralId(
                referralId as string | undefined,
                tempMultiOrdersProgramCost,
                props.programBudget!,
                props.setProgramBudget!,
              );
            } else {
              props.setProgramOrdersCost(tempProgramOrderCost);
              setProgramBudgetByReferralId(
                referralId as string | undefined,
                tempProgramOrderCost,
                props.programBudget!,
                props.setProgramBudget!,
              );
            }
            setConfirmOpen(true);
          }

          props.setOrdersMealEquivalents(orderQuantity + toInt(props.ordersMealEquivalents));
          // props.setProgramOrdersCost(parseInt(props.programOrdersCost) +
          // vendorsFoodPrice?.foodPrice);
          setRenderFoodItemCardFlag(!renderFoodItemCardFlag);
          if (
            VENDORS.FF === props.vendor ||
            VENDORS.SB === props.vendor ||
            VENDORS.OK === props.vendor
          ) {
            setSelectedItemIds(new Set());
            const updatedValue: MemberSelectionMap = {};
            updatedValue[memberKey(props.patientId)] = new Set([]);
            setMemberSelectedItemIds((prev) => ({
              ...prev,
              ...updatedValue,
            }));
            setVariableQtySelectedItemIds({});
          }
          resolve(0);
        },
        () => {
          setLoading(false);
          if (index === undefined) {
            setConfirmType('FAILURE_ORDER');
            setConfirmOpen(true);
          }
          resolve(0);
        },
      );
    });
  };

  const notificationMessage = (type) => {
    switch (type) {
      case 'SUCCESS_ORDER':
        return (
          <>
            <Modal.Header>
              <Icon name="thumbs up outline" size="large" color="orange"></Icon>
              Success
            </Modal.Header>
            <Modal.Content>Order has been created! </Modal.Content>
          </>
        );
      case 'FAILURE_ORDER':
        return (
          <>
            <Modal.Header>
              <Icon name="warning sign" size="large" color="orange"></Icon>Error
            </Modal.Header>
            <Modal.Content>Oops..something went wrong in the order creation! </Modal.Content>
          </>
        );
      case 'FAILURE_ADDRESS_VALIDATION':
        return (
          <>
            <Modal.Header>
              <Icon name="warning sign" size="large" color="orange"></Icon>Error
            </Modal.Header>
            <Modal.Content>
              Oops...Address validation failed. Please update the address in member profile and
              retry!{' '}
            </Modal.Content>
          </>
        );
    }
  };

  const displaySelectedItems = () => {
    const selectedItems = buildOrderItems(
      itemsData,
      memberSelectedItemIds[memberKey(props.patientId)],
      variableQtySelectedItemIds,
      bundleItemsData,
      props.patientId,
    );
    return (
      <>
        {quantityErrorMsg === '' && (
          <h4 style={{ margin: '25px 0 15px 15px', color: '#0067a5', fontSize: '17px' }}>
            {' '}
            Are you sure you want to create an order for the below items
            <Icon name="question" color="red" />
          </h4>
        )}
        {quantityErrorMsg !== '' && (
          <h4 style={{ margin: '25px 0 15px 15px', color: 'red', fontSize: '17px' }}>
            {quantityErrorMsg}
          </h4>
        )}
        <Table celled compact>
          <Table.Header>
            <Table.Row color="red" textAlign="center">
              <Table.HeaderCell>Food Item</Table.HeaderCell>
              <Table.HeaderCell>Qty</Table.HeaderCell>
              <Table.HeaderCell>Servings</Table.HeaderCell>
            </Table.Row>
          </Table.Header>

          <Table.Body>
            {selectedItems.map((item, ind) => {
              return (
                <Table.Row key={ind}>
                  <Table.Cell>
                    <Icon
                      name="food"
                      color="blue"
                      style={{ fontSize: '1.2em', paddingRight: '8px' }}
                    ></Icon>
                    {item.name}
                  </Table.Cell>
                  <Table.Cell textAlign="center">{item.quantity}</Table.Cell>
                  <Table.Cell textAlign="center">{item.noOfServings}</Table.Cell>
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table>
      </>
    );
  };

  const cancelConfirm = () => setConfirmOpen(false);

  //TODO: Create Provenance in FHIR on Firestore Order creation.
  return (
    <div>
      {(datasetLoading || loading || (!bundleItemsData && !bundleItemsError && !itemsData)) && (
        <Loader active inline="centered" />
      )}
      {bundleItemsData && bundleItemsData.length === 0 && (
        <Message warning header="Info" content="0 Bundle Items found!" />
      )}
      {itemsData && itemsData.length === 0 && (
        <Message warning header="Info" content="0 Items found!" />
      )}
      {!datasetLoading && bundleItemsData && bundleItemsData.length > 0 && props.patientId && (
        <>
          {allItemDetails && !orderDeadLine?.[props.vendor]?.['flag'] && (
            <div style={{ textAlign: 'center' }}>
              {VENDORS_ORDERS_PRIVILEGE?.[props.vendor]?.FutureDate && (
                <SemanticDatepicker
                  label="Cut Off Date: &nbsp;"
                  autoComplete="false"
                  value={orderDate}
                  datePickerOnly
                  clearable={false}
                  minDate={moment().add(-1, 'days').toDate()}
                  required
                  name="dob"
                  format="MM/DD/YYYY"
                  id="form-input-control-dob"
                  onChange={(e) =>
                    handleOrderDate(e as unknown as Parameters<typeof handleOrderDate>[0])
                  }
                />
              )}
              {!['AB'].includes(props.vendor) && (
                <>
                  <Button
                    fluid={!VENDORS_ORDERS_PRIVILEGE?.[props.vendor]?.FutureDate}
                    primary
                    onClick={handleCreateOrder}
                    disabled={selectedItemIds.size === 0}
                  >
                    Create Order
                  </Button>
                </>
              )}
            </div>
          )}
          {/*
            SUSPECTED DEFECT — REPORTED, NOT FIXED. This read is unguarded while
            every sibling read of the same table is not: the block above gates on
            `!orderDeadLine?.[props.vendor]?.['flag']` and the card below passes
            `orderDeadLine?.[props.vendor]?.['flag']`. A vendor absent from
            `VENDORS_ORDER_CUTOFFDAY` throws here and takes the catalogue down.

            The `!` keeps that throw rather than moving it. Adding `?.` would not
            be neutral: `undefined !== ''` is TRUE, so an unlisted vendor would
            start rendering an EMPTY warning banner where it currently fails loudly.
          */}
          {orderDeadLine[props.vendor]!['message'] !== '' && (
            <Message warning header="Warning">
              {' '}
              {orderDeadLine[props.vendor]!['message']}
            </Message>
          )}
          {confirmType === 'CREATE_ORDER' && (
            <Confirm
              open={confirmOpen}
              content={displaySelectedItems()}
              cancelButton="Never mind"
              confirmButton="Let's do it"
              onCancel={cancelConfirm}
              onConfirm={() => closeConfirm()}
            />
          )}

          {confirmType !== 'CREATE_ORDER' && (
            <Modal
              open={confirmOpen}
              size="mini"
              content={notificationMessage(confirmType)}
              onActionClick={() => setConfirmOpen(false)}
              actions={[{ key: 'done', icon: 'check', content: 'OK', positive: true }]}
            ></Modal>
          )}
          {confirmType === 'INVALID_QUANTITY' && (
            <Modal
              open={confirmOpen}
              size="small"
              content={displaySelectedItems()}
              onActionClick={() => setConfirmOpen(false)}
              actions={[{ key: 'done', icon: 'check', content: 'Close', positive: true }]}
            ></Modal>
          )}
        </>
      )}

      {!datasetLoading &&
        bundleItemsData &&
        bundleItemsData.length > 0 &&
        bundleItemsData.map((item, index) => {
          return (
            <FoodItemCard
              foodRatings={foodRatings[mapKey(item.itemId)]}
              key={index.toString() + props.patientId}
              onItemClick={handleItemClick}
              onItemCheck={handleItemCheck}
              setItemsQuantity={setItemsQuantity}
              clickedItem={props.clickedItem}
              selectedItemIds={memberSelectedItemIds[memberKey(props.patientId)]}
              patientId={props.patientId}
              item={
                (item.itemId && allItemDetails && allItemDetails[item.itemId]) as
                  | FoodItem
                  | undefined
              }
              vendor={props.vendor}
              renderFoodItemCardFlag={renderFoodItemCardFlag}
              observationsData={filteredObservationsData}
              bundleItem={item}
              orderDeadLine={orderDeadLine?.[props.vendor]?.['flag']}
            />
          );
        })}

      {bundleItemsError && (
        <Message negative>
          <Message.Header>Oops!. Something went wrong.. </Message.Header>
          <p>{bundleItemsError}</p>
        </Message>
      )}
    </div>
  );
});

/**
 * Assembles the `POST /order` body for a catalog bundle order.
 *
 * The four `_`-prefixed parameters are accepted and never read: the caller builds
 * `orderItems` itself and passes it in assembled. Kept rather than deleted because
 * removing them renumbers sixteen positional arguments.
 */
function buildOrder(
  estimatedDeliveryDate: string | undefined,
  orderItems: FoodItem[],
  orderDate: Date,
  /**
   * `pricingCalculator` declares `organization: string` and indexes its pricing tables
   * with it, so an absent org reads the `'undefined'` entry and prices the order at
   * nothing. The standing TS2345 below is that disagreement.
   */
  orgId: string | null | undefined,
  vendorsFoodPrice: VendorPricingByCode,
  vendor: string,
  patientId: string | null | undefined,
  /**
   * SUSPECTED DEFECT — REPORTED, NOT FIXED. The multi-bundle arm of the call site
   * passes `props.allBundles[selectedBundleId]`, an open-keyed lookup that genuinely
   * misses — `allBundles` is keyed by bundle id and `selectedBundleId` is derived from
   * a fetched item at runtime. Every `bundle.*` read in the payload below is unguarded
   * (`bundle.bundleName`, `bundle.bundleId`, `bundle.effectivePeriod!.start`, …), so a
   * miss throws while assembling the order body.
   *
   * A SECOND disagreement sits on the same argument: `allBundles` is declared
   * `Record<string, FoodItem | undefined>`, and `FoodItem` carries none of
   * `bundleTypeCode`, `bundleTypeName`, `flavorCode`, `flavorName`, `vendorBundleId`
   * or `effectivePeriod` — the six bundle-level fields the payload below reads off
   * this argument. So on the multi-bundle path the producer's declared type cannot
   * supply what the consumer reads; either `allBundles` really holds whole
   * `FoodCatalogBundle` rows and is under-declared, or those six fields are `undefined`
   * on every multi-bundle order this builds.
   *
   * Declared as the single-bundle arm's `FoodCatalogBundle` — what the reads below
   * assume — so both disagreements surface as one diagnostic at the call site rather
   * than as thirteen assertions in here that would quietly bless them.
   */
  bundle: FoodCatalogBundle,
  practitionerName: string,
  _items: FoodItem[] | null | undefined,
  _selectedItemIds: Set<unknown>,
  deliveryInstructions: string,
  bundleQuantity: number,
  _variableQtySelectedItemIds: Record<string, number | undefined>,
  _bundleItemsData: FoodItem[] | null | undefined,
  referralId: string | false | undefined,
) {
  const docId = moment().unix().toString() + (Math.floor(Math.random() * 8999) + 1000);
  const currentDateTime = moment().utc().format();

  const selectedOrderDate =
    orderDate === moment().toDate() ? currentDateTime : moment(orderDate).toISOString();

  const payLoad: { foodPricing?: number | undefined; shippingFees?: number | undefined } & Record<
    string,
    unknown
  > = {
    estimatedDeliveryDate: estimatedDeliveryDate,
    vendorId: vendor,
    vendorName: vendor,
    orderId: docId,
    orderType: bundle?.bundleId?.toUpperCase().indexOf('VIP') !== -1 ? 'Promo' : 'catalog', // always 'catalog' for orders placing through Catalog unless its a VIP Bundle
    orderName: bundle.bundleName,
    docId: docId,
    status: ORDER_NEW_STATE,
    patientId: patientId,
    patientOrg: orgId,
    bundleId: bundle.bundleId,
    bundleTypeCode: bundle.bundleTypeCode,
    bundleTypeName: bundle.bundleTypeName,
    flavorCode: bundle.flavorCode,
    flavorName: bundle.flavorName,
    foodTypeCode: bundle.bundleTypeCode,
    foodTypeName: bundle.bundleTypeName,
    vendorBundleId: bundle.vendorBundleId,
    // The unguarded arm of the three-way `effectivePeriod` inconsistency recorded on
    // `FoodOrderBundle`. A `?.` would post an order with an absent period instead of
    // throwing — the silent version of the same bug.
    bundleEffectivePeriod: {
      start: bundle.effectivePeriod!.start,
      end: bundle.effectivePeriod!.end,
    },
    orderDate: selectedOrderDate,
    createdDate: currentDateTime,
    lastUpdated: currentDateTime,
    createdBy: practitionerName,
    bundleName: bundle.bundleName,
    bundleQuantity: bundleQuantity,
    deliveryInstructions: deliveryInstructions,
    orderItems: orderItems,
    referralId: referralId !== '' ? referralId : null,
  };

  // Food pricing calculation
  const price = pricingCalculator(
    orgId!,
    vendor,
    payLoad['orderItems'] as FoodItem[],
    vendorsFoodPrice,
    bundle.bundleName,
  );
  try {
    /*
     * SUSPECTED DEFECT — REPORTED, NOT FIXED. This `try`/`catch` cannot fire: both
     * statements are property assignments onto an object literal, and
     * `pricingCalculator` has already returned. So a pricing failure propagates out of
     * this function rather than being swallowed here as the shape suggests, and
     * `price` being `undefined` puts both fields onto the posted order as `undefined`.
     */
    payLoad['foodPricing'] = price?.foodPrice;
    payLoad['shippingFees'] = price?.shippingFees;
  } catch (err) {
    console.log(err);
  }

  return payLoad;
}

/**
 * Each member of `selectedItemIds` is an item id with the member's `patientId`
 * appended — the `memberKey` scheme that lets one mounted catalog hold selections for
 * several members. The `.replace(patientId, '')` below strips it back off.
 *
 * SUSPECTED DEFECT — REPORTED, NOT FIXED. All five call sites pass `itemsData`, which
 * is seeded `null` and written only from the `/food/item` arm of the bundle
 * `onSuccess`. `items.filter` is the first thing this does, so any of those paths
 * taken before that response resolves — or after a fetch that returned nothing —
 * throws rather than yielding an empty order.
 *
 * Latent because each path sits behind a control the catalog renders only once the
 * bundle has loaded. Not guarded: `?? []` would post an EMPTY order, which is worse
 * than the throw because the care manager would see it succeed.
 */
function buildOrderItems(
  items: FoodItem[] | null | undefined,
  /*
   * SUSPECTED DEFECT — REPORTED, NOT FIXED. Four of five call sites read this out of
   * the open-keyed `memberSelectedItemIds`, so a member with no recorded selection
   * reads `undefined` and `[...undefined]` throws on the next line.
   *
   * Latent because the bundle `onSuccess` seeds the member's bucket before these
   * controls render — but the `patientId`-keyed map exists precisely to allow a path
   * that reaches here without that seeding having run for the CURRENT member.
   */
  selectedItemIds: Set<unknown> | Iterable<unknown> | undefined,
  variableQtySelectedItemIds: Record<string, number | undefined>,
  bundleItemsData: FoodItem[] | null | undefined,
  patientId: string | null | undefined,
) {
  const selectedItemsArray = [...selectedItemIds!];
  /*
   * SUSPECTED DEFECT — REPORTED, NOT FIXED. `String.prototype.replace` with a string
   * pattern replaces only the FIRST occurrence, and it is not anchored. An item whose
   * own `itemId` happens to contain the `patientId` as a substring would have that
   * inner occurrence stripped instead of the appended suffix, and the resulting id
   * would match no catalog item — so the line silently drops out of the order.
   *
   * Latent because `memberKey` appends the id, and the vendors' item ids do not
   * embed patient ids.
   *
   * Asserted rather than coerced with `String(...)`: the two disagree on a non-string
   * member, where `.replace` throws and `String(...)` would stringify it into an id
   * that matches nothing. The throw is the existing behaviour.
   */
  const uniqueSelectedItemIds = selectedItemsArray.map((item) =>
    (item as string).replace(mapKey(patientId), ''),
  );

  const preparedOrderItems = items!.filter((item) => {
    // `includes` is widened, not coerced: an item with no `itemId` correctly matches
    // nothing. The `hasOwnProperty` test and subscript below are the opposite case —
    // both stringify their key, so `mapKey` on both keeps them addressing one entry.
    if ((uniqueSelectedItemIds as (string | undefined)[]).includes(item.itemId)) {
      if (Object.prototype.hasOwnProperty.call(variableQtySelectedItemIds, mapKey(item.itemId))) {
        // This is for vendors with custom quantity selection.
        item.quantity = variableQtySelectedItemIds[mapKey(item.itemId)];
        return item;
      } else {
        let itemPayload: FoodItem | undefined;
        bundleItemsData?.forEach((bundleItem) => {
          if (bundleItem.itemId === item.itemId) {
            item.quantity = bundleItem.quantity;
            itemPayload = item;
          }
        });
        return itemPayload;
      }
    }
  });
  return preparedOrderItems;
}

export default FoodBundleItems;
